import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PfileIds } from '@lib2/model/pfile/PfileTypes';
import type { WidthHeight } from '@lib2/model/types/WidthHeight';
import { PfileApi } from '@/shared/api/PfileApi';
import { S3FileUploader } from '@/features/upload/S3FileUploader';
import { ensureValidCredentials } from '@/features/upload/useUploadCredentials';
import { useAuth } from '@/features/auth/hooks/useAuth';

interface UploadPfileImageParams {
  file: File;
  contentType: string;
  pfileIds: PfileIds;
  posterSize?: WidthHeight;
  thumbSize?: WidthHeight;
}

/**
 * Generic pfile image upload mutation.
 * Creates ingest record → uploads to S3 → returns ingestJobId for job polling.
 *
 * Poster/thumb sizes default to null (backend determines output dimensions
 * based on pfileTypeId).
 */
export function usePfileImageUpload(invalidateQueryKeys: readonly (readonly unknown[])[]) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      contentType,
      pfileIds,
      posterSize,
      thumbSize,
    }: UploadPfileImageParams) => {
      if (!selectedCId) throw new Error('No customer selected');

      const clientIngest = await PfileApi.createIngestPfileImage(selectedCId, {
        ingestMethodType: 'dashboard',
        downloadUrl: null,
        filename: file.name,
        format: 'webp',
        pfileIds,
        poster: posterSize ?? null,
        thumb: thumbSize ?? null,
      });

      if (!clientIngest.dashboard) {
        throw new Error('Backend returned no dashboard ingest data');
      }

      const { key, ingestJobId } = clientIngest.dashboard;

      const credentials = await ensureValidCredentials(selectedCId);
      await S3FileUploader.putObject(credentials, key, file, contentType);

      return ingestJobId;
    },
    onSuccess: () => {
      for (const qk of invalidateQueryKeys) {
        queryClient.invalidateQueries({ queryKey: qk });
      }
    },
  });
}

/**
 * Delete pfile images by file IDs.
 */
export function useDeletePfileImage(invalidateQueryKeys: readonly (readonly unknown[])[]) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileIds: number[]) => {
      if (!selectedCId) throw new Error('No customer selected');
      await PfileApi.deletePfiles(selectedCId, fileIds);
    },
    onSuccess: () => {
      for (const qk of invalidateQueryKeys) {
        queryClient.invalidateQueries({ queryKey: qk });
      }
    },
  });
}
